import {
  flightShopProgressSelector,
  type FlightState,
  getDepartureDate,
  getDestination,
  getFilteredAndSortedDepartureFlightList,
  getOrigin,
  getPassengersTotal,
  getPrediction,
  getReturnDate,
  getSelectedTrip,
  getSelectedTripDetails,
  getTripCategory,
} from "@b2bportal/core-flights";
import { FlightShopStep } from "@b2bportal/core-types";
import { Dealness, getHopperFareRatingName } from "@hopper-b2b/types";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";

export interface AirEntryProperties {
  first_launch?: boolean;
  origin: string;
  destination: string;
  origin_country_code: string;
  destination_country_code: string;
  departure_date: string;
  return_date?: string;
  pax_total: number;
  number_of_active_watches?: number;
  trip_type: "round_trip" | "one_way";
  rewards_accounts?: string;
  advanced: number;
  los: number;
}

export interface ViewedForecastProperties extends AirEntryProperties {
  slice_direction?: "outbound" | "return";
  flights_shown?: number;
  account_type_selected?: string;
  recommendation: "buy" | "wait" | "null";
  dealness: Dealness | string;
  fares_shown_outbound?: number;
  fares_shown_return?: number;
}

export interface SelectedSliceProperties extends ViewedForecastProperties {
  fare_class: string;
  total_stops: number;
}

export const getAirEntryProperties = createSelector(
  getOrigin,
  getDestination,
  getDepartureDate,
  getReturnDate,
  getPassengersTotal,
  getSelectedTripDetails,
  getTripCategory,
  (state: FlightState) => state,
  (
    origin,
    destination,
    departureDate,
    returnDate,
    passengerCount,
    tripDetails,
    tripCategory,
    state
  ): AirEntryProperties => {
    return {
      origin: !origin
        ? tripDetails
          ? tripDetails.slices[0].originName
          : ""
        : `${origin.id.code.regionType}/${origin.id.code.code}`,
      destination: !destination
        ? tripDetails
          ? tripDetails.slices[0].destinationName
          : ""
        : `${destination.id.code.regionType}/${destination.id.code.code}`,
      origin_country_code:
        origin?.id.code.code ||
        (tripDetails ? tripDetails.slices[0].originCode : ""),
      destination_country_code:
        destination?.id.code.code ||
        (tripDetails ? tripDetails.slices[0].destinationCode : ""),
      departure_date: !departureDate
        ? tripDetails
          ? tripDetails.slices[0].departureTime
          : ""
        : dayjs(departureDate).format("YYYY-MM-DD"),
      ...(tripDetails &&
        tripDetails.slices.length > 1 && {
          return_date: dayjs(tripDetails.slices[1].departureTime).format(
            "YYYY-MM-DD"
          ),
        }),
      ...(returnDate && {
        return_date: dayjs(returnDate).format("YYYY-MM-DD"),
      }),
      pax_total: passengerCount,
      trip_type: tripCategory,
      advanced: dayjs(departureDate).diff(dayjs(), "days"),
      los:
        returnDate || (tripDetails && tripDetails.slices.length > 0)
          ? dayjs(returnDate).diff(departureDate, "days")
          : 1,
    };
  }
);

export const getViewedForecastProperties = createSelector(
  getPrediction,
  getAirEntryProperties,
  flightShopProgressSelector,
  getFilteredAndSortedDepartureFlightList,
  (prediction, airEntryProps, shopStep, flights): ViewedForecastProperties => {
    const isInChooseDepartureStep = shopStep === FlightShopStep.departure;
    const isInChooseReturnStep = shopStep === FlightShopStep.return;

    return {
      ...airEntryProps,
      flights_shown:
        isInChooseDepartureStep || isInChooseReturnStep
          ? flights.length
          : undefined,
      slice_direction: isInChooseReturnStep
        ? "return"
        : isInChooseDepartureStep
          ? "outbound"
          : undefined,

      dealness: prediction?.dealness || "",
      recommendation: !prediction
        ? "null"
        : prediction.dealness === Dealness.Wait
          ? "wait"
          : "buy",
    };
  }
);

export const getTotalStops = createSelector(
  getSelectedTripDetails,
  (tripDetails): number => {
    return (
      tripDetails?.slices.reduce((acc, slice) => acc + slice?.stops, 0) || 0
    );
  }
);

export const getSelectedSliceProperties = createSelector(
  getViewedForecastProperties,
  getSelectedTrip,
  getTotalStops,
  flightShopProgressSelector,
  (
    viewedForecastProperties,
    selectedTrip,
    totalStops,
    shopStep
  ): SelectedSliceProperties => {
    const isReturnStep = shopStep === FlightShopStep.return;

    return {
      ...viewedForecastProperties,
      fare_class: getHopperFareRatingName(
        isReturnStep
          ? selectedTrip.returnFareRating
          : selectedTrip.outgoingFareRating
      ),
      total_stops: totalStops,
    };
  }
);
